/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

/* @flow */

import React from 'react';
import Wait from '@react/react-spectrum/Wait';
import Installation from '../AppsPDP/tabs/Installation';
import InformationDialog from '../Enterprise/InformationDialog';
import ModalContainer from '@react/react-spectrum/ModalContainer';
import appsUtil from '../../utils/AppsUtil';
import CompatibilityUtil from '../../utils/CompatibilityUtil';
import {DOWNLOAD_CONTROLLER_USER_SOURCE} from '../../constants/AppPicker/appPickerConstants';
import Url from '../../utils/Url';
import '../AppsController/AppsController.scss';
import InstallationControllerStyles from './InstallationController.scss';
import AppHandlers from '../AppHandlers/AppHandlers';
import IncompatibleDialogUtil from '../../utils/IncompatibleDialogUtil';
import thorUtil from '../../utils/wam/ThorUtil';
import AppPickerUtil from '../../utils/AppPicker/AppPickerUtil';
import AppsAnalytics from '../../analytics/AppsAnalytics';
import AppsPDPScss from '../AppsPDP/AppsPDP.scss';
import DownloadUtil from '../../utils/DownloadUtil';
import {error} from '../../components/ErrorToast/ErrorToast';
import DownloadController from '../AppPicker/components/Installation/DownloadController';
import DialogManager from '@ccxi/ccxi-apps/src/stores/dialogManager';
import Config from '@ccxi/ccxi-apps/src/stores/config';

export const INSTALLATION_STATUSES = {
	ERROR: {
		FFC_DATA_INVALID: 'FFC all versions data invalid',
		FETCH_FFC_DATA_FAILED: 'Failed to fetch ffc data',
		INCOMPATIBILITY_DIALOG_OPEN: 'Version Incompatible'
	},
	SUCCESS: {
		ENTERPRISE_DIALOG_OPEN: 'Enterprise Dialog Open',
		INSTALLATION_DIALOG_OPEN_THOR: 'Thor Installation Dialog Open',
		INSTALLATION_DIALOG_OPEN_KAIZEN: 'Kaizen Installation Dialog Open',
		INSTALLATION_UI_COMPONENT_READY_TO_MOUNT: 'Installation Ui Component Ready to Mount'
	}
};

type InstallationConfig = {
	sapcode: string,
	parentContext?: Object,
	isCCPPWorkflow?: boolean,
	history?: Object, /* Needed for DownloadController */
	shouldReturnDnldComponent?: boolean, /* If this is true, download component is returned instead of opening full page install dialog */
	beforeIncompatibilityDialogOpen?: Function,
	loadHomePage?: Function,
	onCloseHandler?: Function,
	tryWamInstallation?: Function,
	executeCommand?: Function,
	hideToast?: Function,
	customerSegment?: string,
	openPdpPage?: Function
};

type InitialConfig = {
	locale?: string,
	intl: Object,
	environment: string,
	hasThor?: boolean
}

function noop() {}

function logReqFieldError(fieldName) {
	throw new Error(`${fieldName} missing in installation controller constructor`);
}

export default class InstallationController {
	locale: string;
	intl: Object;
	currentPDPConfig: Object;
	hasThor: ?boolean;
	environment: string;
	currentSapCode: string; /* To keep track of the current sapcode being displayed */
	installationStyleProps: Object;
	informationDialogKey: Number | null;
	dialogKey: Number | null;
	downloadSuccessNotify: Function;
	downloadErrorNotify: Function;
	installationPromise: Promise<string> | null;
	_genericPDPAnalyticEvent: Function;
	_onAppDetailEvent: Function;
	hideModalContainer: Function;

	constructor(config: InitialConfig) {
		if (!config.intl) {
			logReqFieldError('intl');
		}
		if (!config.environment) {
			logReqFieldError('environment');
		}
		window.ENV = window.ENV || config.environment;

		this.installationStyleProps = {
			appsPDPWrapper: AppsPDPScss.appsPDPWrapper,
			header: AppsPDPScss.header,
			toolBar: AppsPDPScss.toolBar,
			closeContainer: AppsPDPScss.closeContainer,
			closeIcon: AppsPDPScss.closeIcon
		};
		this.informationDialogKey = null;
		this.dialogKey = null;
		this.currentPDPConfig = {};
		this.locale = config.locale || 'en';
		this.intl = config.intl;
		this.hasThor = config.hasThor;
		this.environment = config.environment;
		this.downloadSuccessNotify = noop;
		this.downloadErrorNotify = noop;
		this.installationPromise = null;

		this._onAppDetailEvent = this._onAppDetailEvent.bind(this);
		this._genericPDPAnalyticEvent = this._genericPDPAnalyticEvent.bind(this);

		// to initialise Dialog for incompatible scenerios
		DialogManager.init(Config.dialogManager);
		appsUtil.intl = config.intl;
		appsUtil.locale = config.locale || 'en';
	}

	_isAppCompatible(sapcode: string) {
		const productConfig = appsUtil.getFFCProductConfigBySapCode(sapcode);
		return CompatibilityUtil.isAppSupported(productConfig.id);
	}

	// Check for single app starter page and pdp installation page
	_checkInstallationRoute() {
		return (appsUtil.getPathData().mainAppsTab === 'starter' || Url.getParam('isStarter') === 'true') && this.currentPDPConfig;
	}

	_onAppDetailEvent(eventType: string, eventSubtype: string, url?: string, guid?: string) {
		const appsAnalytics = new AppsAnalytics(this.environment);
		appsAnalytics.initAnalytics();
		appsAnalytics.onAppDetailEvent(this.currentPDPConfig, eventType, eventSubtype, url, guid);
	}

	_genericPDPAnalyticEvent(eventType: string,eventSubtype: string, viewType: string, expCampaignID: string, expVariationID: string) {
		const appsAnalytics = new AppsAnalytics(this.environment);
		appsAnalytics.initAnalytics();
		appsAnalytics.genericPDPAnalyticEvent(this.currentPDPConfig, eventType, eventSubtype, viewType, expCampaignID, expVariationID);
	}

	_onAppDownloadEvent = (isRetry: ?boolean) => {
		const appsAnalytics = new AppsAnalytics(this.environment);
		appsAnalytics.initAnalytics();
		appsAnalytics.onAppDownloadEvent(this.currentPDPConfig, 'App Detail', isRetry ? 'retry' : undefined);
	}

	_getDownloadComponent(sapCode: string, history: Object) {
		return (
			<DownloadController
				key={`single-app-download-view-${sapCode}`}
				productId={appsUtil.getProductIdFromSapcode(sapCode)}
				source={DOWNLOAD_CONTROLLER_USER_SOURCE.SINGLE_APP_POST_PURCHASE_PAGE}
				locale={this.locale}
				environment={this.environment}
				openGetHelpWidget={true}
				hasThor={this.hasThor}
				history={history}
				intl={this.intl}
			/>
		);
	}

	initiateAppDownloadWorkflow({sapcode, openPdpPage = noop,...installationConfig}: InstallationConfig): Promise<string> {
		this.installationPromise = this.installationPromise || new Promise((resolve, reject) => {
			this.downloadSuccessNotify = resolve;
			this.downloadErrorNotify = reject;
			this.currentSapCode = sapcode;

			if (typeof this.hasThor === 'undefined') {
				this.hasThor = appsUtil.hasThor;
			}

			if (installationConfig.shouldReturnDnldComponent) {
				this.downloadSuccessNotify({
					status: INSTALLATION_STATUSES.SUCCESS.INSTALLATION_UI_COMPONENT_READY_TO_MOUNT,
					dnldUIComponent: this._getDownloadComponent(sapcode, installationConfig.history)
				});
				setTimeout(() => {
					/**
					 * Keeping this inside settimmeout of zero to ensure this is execute asynchronously.
					 * Without this timeout, the installationPromise will never be set to null.
					 */
					this.installationPromise = null;
				}, 0);
			}
			else {
				const modalDependencies = [DownloadUtil.setFFCAllVersionData(sapcode), DownloadUtil.getThorStatus(this.hasThor)];
				let loaderKey = ModalContainer.show(
					<div mode='fullscreenTakeover' className={InstallationControllerStyles.appDownloadLoader}>
						<Wait size='L' />
					</div>,
					installationConfig.parentContext
				);

				Promise.all(modalDependencies).then(([ffcDataFetched, hasThor]) => {
					ModalContainer.hide(loaderKey);
					loaderKey = null;
					this.hasThor = hasThor;
					if (!ffcDataFetched) {
						this.downloadErrorNotify(INSTALLATION_STATUSES.ERROR.FFC_DATA_INVALID);
						this.installationPromise = null;
					}

					if (!hasThor
						&& appsUtil.isSubstanceDesktopApp(sapcode) && !appsUtil.isMobileOrTab()) {
						if (appsUtil.getFFCProductConfigBySapCode(sapcode) && appsUtil.isAppEntitled(sapcode, 'desktop')) {
							error(this.intl.formatMessage({id: 'apps.pdp.CCXUI.substanceApp.kaizenBuild.unavailable'}), {msgId: 'KaizenUnavailableSBST', variant: 'default', timeout: 7000});
							openPdpPage(sapcode);
						}
					}
					else if (!hasThor && appsUtil.isUserEnterpriseUser()) {
						const informationDialog = document.querySelector('.informationDialog');
						if (!informationDialog && !this.informationDialogKey) {
							if (appsUtil.isAppNotEntitledForEnterpriseUser(sapcode)) {
								this._checkForWamInstallation({sapcode, ...installationConfig});
							}
							else {
								if (this._isAppCompatible(sapcode)) {
									this._openEnterpriseDialog({sapcode, ...installationConfig});
								}
								else {
									this._checkForWamInstallation({sapcode, ...installationConfig});
								}
							}
						}
					}
					else {
						this._checkForWamInstallation({sapcode, ...installationConfig});
					}
				})
					.catch(() => {
						if (loaderKey) {
							ModalContainer.hide(loaderKey);
						}
						this.downloadErrorNotify(INSTALLATION_STATUSES.ERROR.FETCH_FFC_DATA_FAILED);
						this.installationPromise = null;
					});
			}
		});
		return this.installationPromise;
	}

	_openEnterpriseDialog({sapcode, parentContext, ...installationConfig}: InstallationConfig) {
		const dialogConfig = {
			enterpriseUserWF: true,
			title: this.intl.formatMessage({id: 'apps.pdp.enterprise.user.permission.warning.title'}),
			desc: this.intl.formatMessage({id: 'apps.pdp.enterprise.user.permission.warning.desc'}),
			OK: {
				value: this.intl.formatMessage({id: 'apps.confirmation.btn.ok'}),
				onClick: () => {
					ModalContainer.hide(dialogKey);
					this.informationDialogKey = null;
					this._checkForWamInstallation({sapcode, parentContext, ...installationConfig});
				}
			}
		};
		this.downloadSuccessNotify(INSTALLATION_STATUSES.SUCCESS.ENTERPRISE_DIALOG_OPEN);
		this.installationPromise = null;
		const dialogKey = ModalContainer.show(<InformationDialog dialogConfig={dialogConfig}/>, parentContext);
		this.informationDialogKey = dialogKey;
	}

	_checkForWamInstallation({sapcode, tryWamInstallation = noop, ...installationConfig}: InstallationConfig) {
		const productId = appsUtil.getProductIdFromSapcode(sapcode);
		let wamConnectionEstablished = false;
		this.currentPDPConfig = appsUtil.getFFCProductConfig(productId);

		if (!AppPickerUtil.isNujM3PhotoPlanTestExperience() && this.currentPDPConfig && thorUtil.isConnected()) {
			wamConnectionEstablished = tryWamInstallation(
				sapcode,
				this.currentPDPConfig,
				this._openInstallationDialog.bind(this, {sapcode, ...installationConfig})
			);
		}

		if (!wamConnectionEstablished) {
			this._openInstallationDialog({sapcode, ...installationConfig});
		}
	}

	_openInstallationDialog({sapcode, parentContext, isCCPPWorkflow = false, shouldReturnDnldComponent, onCloseHandler = noop, loadHomePage = noop, beforeIncompatibilityDialogOpen = noop, executeCommand = noop, hideToast = noop, customerSegment}: InstallationConfig) {
		if (!sapcode) {
			return;
		}

		if (!appsUtil.customerSegment && customerSegment) {
			appsUtil.customerSegment = customerSegment;
		}

		const productId = appsUtil.getProductIdFromSapcode(sapcode);
		this.currentPDPConfig = appsUtil.getFFCProductConfig(productId);

		if (this.currentPDPConfig) {
			this.currentPDPConfig.entitled = appsUtil.isAppEntitledNew(this.currentPDPConfig, this.currentPDPConfig.id, this.currentPDPConfig.type);
			this.currentPDPConfig.sapCode = this.currentPDPConfig.id;
			if (this.currentPDPConfig.type.toLowerCase() === 'desktop') {
				const isSomeVersionCompatible = this._isAppCompatible(sapcode) || CompatibilityUtil.isAnyOlderVersionSupported(this.currentPDPConfig.id);
				const isTouchDevice = appsUtil.isMobileOrTab();
				if (!appsUtil.isAppNotEntitledForEnterpriseUser(sapcode) && (isSomeVersionCompatible || isTouchDevice)) {
					if (appsUtil.hasFeature('temp_send_link_for_desktop_apps') && isTouchDevice) {
						// Open send link dialog if not already opened
						if (!AppHandlers.sendLinkDialog && document.getElementsByClassName('send-link-dialog').length === 0) {
							let appModel = appsUtil.CCXUIAppStore[`${this.currentPDPConfig.id}_${this.currentPDPConfig.version}`];
							if (!appModel) {
								appModel = {
									sapcode,
									productId,
									appFamilyName: this.currentPDPConfig.displayName,
									platform: 'desktop',
									codexVersion: this.currentPDPConfig.version
								};
							}
							// open pdp before opening send me link dialog.
							beforeIncompatibilityDialogOpen(sapcode, this.currentPDPConfig.type);
							requestAnimationFrame(() => {
								AppHandlers.openSendMeLinkDialog(
									'send-link',
									appModel,
									{source: Url.getParam('source')}
								);
							});
						}
						return;
					}
					if (document.getElementsByClassName('InstallationDialogContent').length === 0) {
						// Open Installation dialog if not touch device and installation dialog not already open
						this._onAppDownloadEvent();
						if (this.hasThor && sapcode !== 'KCCC') {
							this.downloadSuccessNotify(INSTALLATION_STATUSES.SUCCESS.INSTALLATION_DIALOG_OPEN_THOR);
							this.installationPromise = null;
						}
						else {
							this.downloadSuccessNotify(INSTALLATION_STATUSES.SUCCESS.INSTALLATION_DIALOG_OPEN_KAIZEN);
							this.installationPromise = null;
						}
						if (!shouldReturnDnldComponent) {
							this.dialogKey = ModalContainer.show(
								<Installation
									environment={this.environment}
									locale={this.locale}
									checkIsCCPPWorkflow={isCCPPWorkflow}
									loadHomePage={loadHomePage}
									executeCommand={executeCommand}
									genericPDPAnalyticEvent={this._genericPDPAnalyticEvent}
									onAppDetailEvent={this._onAppDetailEvent}
									PDPCSSClassNames={this.installationStyleProps}
									isFullPage={true}
									thorPresent={this.hasThor}
									productSapCode={this.currentPDPConfig.id}
									productName={this.currentPDPConfig.displayName}
									platform={this.currentPDPConfig.type.toLowerCase()}
									productIconUrl={appsUtil.getProductIcon(this.currentPDPConfig)}
									formatMessage={this.intl.formatMessage}
									intl={this.intl}
									productId={productId}
									closeInstallationDialog={this.closeInstallationDialog.bind(this, onCloseHandler)}
									closePDPDialog={this.resetStarterPlanAndCloseDialog.bind(this, onCloseHandler)}
									_onAppDownloadEvent={this._onAppDownloadEvent}
								/>
								, parentContext
							);
						}

						if (appsUtil.hideCCInstallationToast) {
							appsUtil.hideCCInstallationToast();
						}
						else {
							hideToast();
						}
					}
				}
				else {
					beforeIncompatibilityDialogOpen(sapcode, this.currentPDPConfig.type);
					if (!IncompatibleDialogUtil.appDialog) {
						let appModel = appsUtil.CCXUIAppStore[`${sapcode}_${appsUtil.appsFFCProductList[sapcode].version}`];
						if (!appModel) {
							appModel = {
								sapcode,
								productId,
								appFamilyName: this.currentPDPConfig.displayName,
								platform: 'desktop',
								codexVersion: this.currentPDPConfig.version
							};
						}
						this.downloadErrorNotify(INSTALLATION_STATUSES.ERROR.INCOMPATIBILITY_DIALOG_OPEN);
						this.installationPromise = null;
						IncompatibleDialogUtil.renderDialog(appModel);
					}
					else {
						this.installationPromise = null;
					}
				}
			}
		}
	}

	hideModalContainer(key: string) {
		// flow computed property workaround
		const self: Object = this;
		if (self[key]) {
			ModalContainer.hide(self[key]);
			self[key] = null;
		}
	}

	closeInstallationDialog(onCloseHandler: Function = noop) {
		if (this.dialogKey) {
			document.getElementsByTagName('body')[0].style.overflow = '';
			this.hideModalContainer('dialogKey');
			onCloseHandler();
		}
	}

	showIncompatibilityDialog(appModel: string) {
		IncompatibleDialogUtil.renderDialog(appModel);
	}

	hideIncompatibilityDialog() {
		if (IncompatibleDialogUtil.appDialog) {
			IncompatibleDialogUtil.hideDialog();
		}
	}

	// To prevent history.push on catalog page (Bug fix for multiple back button to be pressed to go back, behind ff temp_bug_fix_cch_8089)
	resetStarterPlanAndCloseDialog(onCloseHandler: Function = noop, preventCallOnCloseHandler: boolean) {
		appsUtil.starterRedirectPlanCode = null;
		this.closeInstallationDialog(!!preventCallOnCloseHandler ? noop : onCloseHandler);
	}

	shouldReRenderDownloadComponent() {
		return appsUtil.getCCWebPathData().product !== appsUtil.getProductIdFromSapcode(this.currentSapCode);
	}
}
